/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// =============================================================================
// Badge de status do item de roteirização.
// Espelha o padrão StatusPill consolidado em BillingCharges.jsx — mesmas cores
// (verde/cinza/vermelho) e mesmo formato de pill.
// =============================================================================

const StatusPill = styled.span`
   display: inline-flex;
   align-items: center;
   justify-content: center;
   padding: ${({ $lg }) => ($lg ? '2px 14px' : '4px 10px')};
   border-radius: 999px;
   font-size: ${({ $lg }) => ($lg ? '20px' : '11px')};
   line-height: 1.25;
   font-weight: 800;
   letter-spacing: ${({ $lg }) => ($lg ? '0.5px' : '0')};
   text-transform: ${({ $lg }) => ($lg ? 'uppercase' : 'none')};
   border: 1px solid ${({ $color }) => $color ?? 'rgba(0,0,0,0.15)'};
   background: ${({ $bg }) => $bg ?? 'rgba(0,0,0,0.06)'};
   color: ${({ $text }) => $text ?? '#444'};
   white-space: nowrap;

   @media (min-width: 768px) {
      font-size: ${({ $lg }) => ($lg ? '24px' : '11px')};
   }
`;

// Paleta padrão — badge sobre superfície clara (tabela da listagem, cards).
const STATUS_CONFIG = {
   PENDENTE: {
      label: 'Pendente',
      bg: 'rgba(0,0,0,0.06)',
      border: 'rgba(0,0,0,0.15)',
      text: '#444',
   },
   REALIZADO: {
      label: 'Realizado',
      bg: 'rgba(46,125,50,0.12)',
      border: 'rgba(46,125,50,0.35)',
      text: '#1b5e20',
   },
   NAO_REALIZADO: {
      label: 'Não realizado',
      bg: 'rgba(211,47,47,0.12)',
      border: 'rgba(211,47,47,0.35)',
      text: '#b71c1c',
   },
   CANCELADO: {
      label: 'Cancelado',
      bg: 'rgba(0,0,0,0.04)',
      border: 'rgba(0,0,0,0.18)',
      text: '#888',
   },
};

// Paleta para fundo escuro (cabeçalho da ficha) — cores sólidas/luminosas pra
// destacar contra o background app. Reutiliza os labels da paleta padrão.
const STATUS_CONFIG_ON_DARK = {
   PENDENTE: {
      bg: 'rgba(255,255,255,0.16)',
      border: 'rgba(255,255,255,0.4)',
      text: '#f0f0f0',
   },
   REALIZADO: {
      bg: '#2e7d32',
      border: 'transparent',
      text: '#ffffff',
   },
   NAO_REALIZADO: {
      bg: '#c62828',
      border: 'transparent',
      text: '#ffffff',
   },
   CANCELADO: {
      bg: 'rgba(255,255,255,0.1)',
      border: 'rgba(255,255,255,0.28)',
      text: 'rgba(255,255,255,0.72)',
   },
};

export default function ItemStatusBadge({ value, size = 'sm', onDark = false }) {
   const label = STATUS_CONFIG[value]?.label ?? value ?? '—';
   const palette =
      (onDark ? STATUS_CONFIG_ON_DARK[value] : STATUS_CONFIG[value]) ?? {};

   return (
      <StatusPill
         $lg={size === 'lg'}
         $bg={palette.bg}
         $color={palette.border}
         $text={palette.text}
      >
         {label}
      </StatusPill>
   );
}

ItemStatusBadge.propTypes = {
   value: PropTypes.oneOf([
      'PENDENTE',
      'REALIZADO',
      'NAO_REALIZADO',
      'CANCELADO',
   ]),
   size: PropTypes.oneOf(['sm', 'lg']),
   onDark: PropTypes.bool,
};
